import type { JobStatus, Role } from './types';

const dateTime = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'medium' });
const shortDateTime = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const number = new Intl.NumberFormat('es-ES');
const relative = new Intl.RelativeTimeFormat('es-ES', { numeric: 'auto' });

export const fmtNumber = (n: number) => number.format(n);

/** Importes; por debajo de 1 se muestran hasta 4 decimales (Textract cobra fracciones de céntimo). */
export function fmtMoney(value: number, currency: 'EUR' | 'USD'): string {
  return value.toLocaleString('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: value > 0 && value < 1 ? 4 : 2,
  });
}
export const fmtDay = (isoDate: string) => new Date(`${isoDate}T12:00:00Z`).toLocaleDateString('es-ES');
export const fmtDate = (iso: string | null) => (iso ? dateTime.format(new Date(iso)) : '—');
export const fmtShortDate = (iso: string | null) => (iso ? shortDateTime.format(new Date(iso)) : '—');

export function fmtBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toLocaleString('es-ES', { maximumFractionDigits: v < 10 ? 1 : 0 })} ${units[i]}`;
}

export function fmtDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toLocaleString('es-ES', { maximumFractionDigits: 1 })} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

export function fmtRelative(iso: string | null): string {
  if (!iso) return 'nunca';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return relative.format(Math.round(diff), 'second');
  if (abs < 3600) return relative.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return relative.format(Math.round(diff / 3600), 'hour');
  return relative.format(Math.round(diff / 86400), 'day');
}

export const STATUS_LABEL: Record<JobStatus, string> = {
  queued: 'En cola',
  processing: 'Procesando',
  done: 'Completado',
  failed: 'Error',
  cancelled: 'Cancelado',
};

export const ROLE_LABEL: Record<Role, string> = {
  superadmin: 'Superadmin',
  admin: 'Administrador',
  user: 'Usuario',
  viewer: 'Lector',
};

export const ROLE_HELP: Record<Role, string> = {
  superadmin: 'Acceso total a todos los grupos',
  admin: 'Gestiona usuarios, API keys y trabajos de su grupo',
  user: 'Envía documentos y ve los trabajos del grupo',
  viewer: 'Solo ve y descarga los trabajos del grupo',
};

export const AUDIT_LABEL: Record<string, string> = {
  'auth.login': 'Inicio de sesión',
  'auth.login_failed': 'Intento de acceso fallido',
  'auth.logout': 'Cierre de sesión',
  'auth.password_changed': 'Cambio de contraseña',
  'group.created': 'Grupo creado',
  'group.updated': 'Grupo modificado',
  'user.created': 'Usuario creado',
  'user.updated': 'Usuario modificado',
  'user.deleted': 'Usuario eliminado',
  'api_key.created': 'API key creada',
  'api_key.revoked': 'API key revocada',
  'job.cancelled': 'Trabajo cancelado',
  'job.deleted': 'Archivos de trabajo borrados',
  'agent.deleted': 'Agente quitado',
};

export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
